const API = 'https://api.reviews.io/timeline/data';
const MEDIA = 'https://d1011j0lbv5k1u.cloudfront.net/assets/';

const photoUrl = (file, size) =>
  `https://media.reviews.co.uk/resize/create?format=jpg&height=${size}&width=${size}&src=${encodeURIComponent(MEDIA + file)}`;

const esc = (text) =>
  String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const fill = (template, values) => template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? '');

const starPath = 'M7 0l1.9 4.4 4.7.4-3.6 3.1 1.1 4.6L7 10l-4.1 2.5 1.1-4.6L.4 4.8l4.7-.4z';

const stars = (rating, label) => {
  const pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
  const row = Array.from({ length: 5 }, () => `<svg viewBox="0 0 14 13" aria-hidden="true"><path d="${starPath}"/></svg>`).join('');
  return `<span class="drv-rio__stars" role="img" aria-label="${esc(label)}"><span class="drv-rio__stars-bg">${row}</span><span class="drv-rio__stars-fill" style="width:${pct}%">${row}</span></span>`;
};

class DrvRio {
  constructor(root) {
    this.root = root;
    this.d = root.dataset;
    this.page = 1;
    this.sort = 'date_desc';
    this.rating = 0;
    this.media = '';
    this.search = '';
    this.total = 0;
    this.shown = 0;
    this.first = true;
    this.req = 0;
    this.topics = {};
    this.summary = root.querySelector('[data-rio-summary]');
    this.ai = root.querySelector('[data-rio-ai]');
    this.list = root.querySelector('[data-rio-list]');
    this.more = root.querySelector('[data-rio-more]');
    this.toolbar = root.querySelector('[data-rio-toolbar]');
    this.menu = root.querySelector('[data-rio-sort]');
    this.searchInput = root.querySelector('[data-rio-search]');
    this.more.addEventListener('click', () => this.load(this.page + 1));
    this.menu.addEventListener('click', (e) => {
      const sort = e.target.closest('[data-rio-sort-value]');
      const media = e.target.closest('[data-rio-with-value]');
      const rating = e.target.closest('[data-rio-rating]');
      const clear = e.target.closest('[data-rio-clear-all]');
      if (!sort && !media && !rating && !clear) return;
      if (sort) {
        if (sort.dataset.rioSortValue === this.sort) return this.closeMenu(true);
        this.sort = sort.dataset.rioSortValue;
      } else if (media) {
        this.media = this.media === media.dataset.rioWithValue ? '' : media.dataset.rioWithValue;
      } else if (rating) {
        const n = Number(rating.dataset.rioRating);
        this.setRating(this.rating === n ? 0 : n);
      } else {
        this.clearFilters();
      }
      this.closeMenu(true);
      this.reload();
    });
    document.addEventListener('click', (e) => {
      if (this.menu.open && !this.menu.contains(e.target)) this.closeMenu(false);
    });
    this.menu.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.open) this.closeMenu(true);
    });
    if (this.searchInput) {
      let timer = null;
      const run = () => {
        const text = this.searchInput.value.trim();
        if (text === this.search) return;
        this.search = text;
        this.reload();
      };
      this.searchInput.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(run, 400);
      });
      this.searchInput.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(timer);
        run();
      });
    }
    this.summary.addEventListener('click', (e) => {
      const bar = e.target.closest('[data-rio-rating]');
      if (!bar) return;
      const n = Number(bar.dataset.rioRating);
      this.setRating(this.rating === n ? 0 : n);
      this.reload();
    });
    this.list.addEventListener('click', (e) => {
      if (!e.target.closest('[data-rio-clear]')) return;
      this.clearFilters();
      this.reload();
    });
    this.ai?.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-rio-topic]');
      if (chip) this.showTopic(chip.dataset.rioTopic);
    });
    this.load(1);
  }

  setRating(n) {
    this.rating = n;
    this.root.querySelectorAll('[data-rio-rating]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.rioRating) === n)));
    this.summary.querySelector('.drv-rio__bars')?.classList.toggle('is-filtered', n > 0);
  }

  clearFilters() {
    this.setRating(0);
    this.media = '';
    this.search = '';
    if (this.searchInput) this.searchInput.value = '';
  }

  closeMenu(focus) {
    this.menu.open = false;
    if (focus) this.menu.querySelector('summary').focus();
  }

  syncMenu() {
    this.menu.querySelectorAll('[data-rio-sort-value]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.rioSortValue === this.sort)));
    this.menu.querySelectorAll('[data-rio-with-value]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.rioWithValue === this.media)));
    const active = (this.rating ? 1 : 0) + (this.media ? 1 : 0) + (this.search ? 1 : 0);
    const count = this.menu.querySelector('[data-rio-sort-count]');
    count.textContent = `(${active})`;
    count.hidden = !active;
    this.menu.querySelector('[data-rio-clear-all]').hidden = !active;
  }

  reload() {
    this.list.innerHTML = '';
    this.shown = 0;
    this.load(1);
  }

  params(page, perPage) {
    return new URLSearchParams({
      type: 'product_review',
      store: this.d.store,
      sort: this.sort,
      page: String(page),
      per_page: String(perPage),
      sku: this.d.skus,
      enable_avatars: 'true',
      must_have_body: '1',
      lang: 'en',
    });
  }

  url(page) {
    const params = this.params(page, this.d.perPage || '6');
    if (this.rating) params.set('rating', String(this.rating));
    if (this.media) params.set('with', this.media);
    if (this.search) params.set('search_text', this.search);
    if (this.first && this.ai) params.set('include_sentiment_analysis', 'true');
    return `${API}?${params}`;
  }

  async load(page) {
    const req = ++this.req;
    this.more.disabled = true;
    this.root.setAttribute('aria-busy', 'true');
    this.syncMenu();
    try {
      const res = await fetch(this.url(page));
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      if (req !== this.req) return;
      this.page = page;
      if (this.first) {
        this.renderSummary(data);
        this.renderAi(data.sentimentAnalysis);
        this.first = false;
      }
      const items = (data.timeline || []).map((t) => t._source).filter(Boolean);
      this.total = Number(data.stats?.results_count || 0);
      this.list.insertAdjacentHTML('beforeend', items.map((r) => this.item(r)).join(''));
      this.shown += items.length;
      if (!this.shown) {
        this.list.innerHTML = this.rating || this.media || this.search
          ? `<li class="drv-rio__empty">${esc(this.d.labelNoMatch)} <button type="button" class="drv-rio__clear" data-rio-clear>${esc(this.d.labelClear)}</button></li>`
          : `<li class="drv-rio__empty">${esc(this.d.labelEmpty)}</li>`;
      }
      this.more.hidden = this.shown >= this.total || !items.length;
    } catch (e) {
      if (req !== this.req) return;
      if (this.first) this.root.hidden = true;
      else if (page === 1) this.list.innerHTML = `<li class="drv-rio__empty">${esc(this.d.labelNoMatch)} <button type="button" class="drv-rio__clear" data-rio-clear>${esc(this.d.labelClear)}</button></li>`;
    } finally {
      if (req === this.req) {
        this.more.disabled = false;
        this.root.removeAttribute('aria-busy');
      }
    }
  }

  renderAi(analysis) {
    if (!this.ai) return;
    const map = analysis?.summarization_json || {};
    const names = Object.keys(map).filter((k) => typeof map[k] === 'string' && map[k].trim());
    if (!names.length) return;
    this.topics = map;
    this.ai.querySelector('[data-rio-ai-tabs]').innerHTML = names
      .map((n) => `<button type="button" class="drv-rio__topic" data-rio-topic="${esc(n)}" aria-pressed="false">${esc(n)}</button>`)
      .join('');
    this.showTopic(names[0]);
    this.ai.hidden = false;
  }

  showTopic(name) {
    if (!this.topics[name]) return;
    this.ai.querySelector('[data-rio-ai-text]').textContent = this.topics[name];
    this.ai.querySelectorAll('[data-rio-topic]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.rioTopic === name)));
  }

  renderSummary(data) {
    const avg = Number(data.stats?.average_rating || 0);
    const count = Number(data.stats?.review_count || 0);
    if (!count) {
      this.summary.innerHTML = '';
      return;
    }
    const buckets = data.aggregations?.ratings?.buckets || [];
    const byStar = Object.fromEntries(buckets.map((b) => [Number(b.key), Number(b.doc_count)]));
    const bucketTotal = buckets.reduce((sum, b) => sum + Number(b.doc_count), 0) || 1;
    const bars = [5, 4, 3, 2, 1]
      .map((s) => {
        const n = byStar[s] || 0;
        return `<li class="drv-rio__bar"><button type="button" class="drv-rio__bar-btn" data-rio-rating="${s}" aria-pressed="false" aria-label="${esc(fill(this.d.labelFilter, { rating: s }))} (${n})"${n ? '' : ' disabled'}><span class="drv-rio__bar-label">${s}<svg viewBox="0 0 14 13" aria-hidden="true"><path d="${starPath}"/></svg></span><span class="drv-rio__bar-track"><span class="drv-rio__bar-fill" style="width:${(n / bucketTotal) * 100}%"></span></span><span class="drv-rio__bar-count">${n}</span></button></li>`;
      })
      .join('');
    this.summary.innerHTML = `
      <div class="drv-rio__score">
        <span class="drv-rio__avg">${avg.toFixed(1)}</span>
        <div class="drv-rio__score-meta">
          ${stars(avg, fill(this.d.labelStars, { rating: avg.toFixed(1) }))}
          <span class="drv-rio__count">${esc(fill(this.d.labelBased, { count: count.toLocaleString('en-GB') }))}</span>
        </div>
      </div>
      <ul class="drv-rio__bars" role="list">${bars}</ul>`;
    const rows = [5, 4, 3, 2, 1].filter((s) => byStar[s]);
    const radio = this.menu.querySelector('.drv-rio__radio')?.outerHTML || '';
    this.menu.querySelector('[data-rio-breakdown-list]').innerHTML = rows
      .map((s) => `<li><button type="button" class="drv-rio__sort-option" data-rio-rating="${s}" aria-pressed="false" aria-label="${esc(fill(this.d.labelFilter, { rating: s }))} (${byStar[s]})">${radio}${stars(s, fill(this.d.labelStars, { rating: s }))}<span class="drv-rio__sort-n">${byStar[s].toLocaleString('en-GB')}</span></button></li>`)
      .join('');
    this.menu.querySelector('[data-rio-breakdown]').hidden = !rows.length;
    this.toolbar.hidden = false;
  }

  item(r) {
    const verified = r.order_id || r.verified_by_shop ? `<span class="drv-rio__verified">${esc(this.d.labelVerified)}</span>` : '';
    const recommend = r.would_recommend_product === 1 ? `<span class="drv-rio__recommend">${esc(this.d.labelRecommend)}</span>` : '';
    const details = this.d.showDetails === 'true'
      ? (r.user_attributes || [])
          .filter((a) => a.answer && a.attribute?.public_label && !a.attribute.private && !a.attribute.deleted && a.attribute.type !== 'yesno')
          .map((a) => `<div class="drv-rio__attr"><dt>${esc(a.attribute.public_label)}:</dt><dd>${esc(a.answer)}</dd></div>`)
          .join('')
      : '';
    const title = r.review_title ? `<p class="drv-rio__title">${esc(r.review_title)}</p>` : '';
    const body = r.comments ? `<p class="drv-rio__body">${esc(r.comments)}</p>` : '';
    const photos = (r.images || [])
      .filter((img) => img?.image)
      .slice(0, 4)
      .map((img) => `<a class="drv-rio__photo-link" href="${esc(photoUrl(img.image, 900))}" target="_blank" rel="noopener" aria-label="${esc(this.d.labelPhoto)}"><img class="drv-rio__photo" src="${esc(photoUrl(img.image, 160))}" alt="" loading="lazy" width="80" height="80"></a>`)
      .concat(
        (r.videos || [])
          .filter((v) => v?.video_url)
          .slice(0, 2)
          .map((v) => `<video class="drv-rio__photo drv-rio__video" src="${esc(MEDIA + v.video_url)}" controls playsinline preload="metadata"></video>`)
      )
      .join('');
    return `
      <li class="drv-rio__item">
        <div class="drv-rio__who">
          <p class="drv-rio__author">${esc(r.author || '')}</p>
          ${verified}
          ${r.address ? `<p class="drv-rio__place">${esc(r.address)}</p>` : ''}
          ${details ? `<dl class="drv-rio__attrs">${details}</dl>` : ''}
        </div>
        <div class="drv-rio__what">
          <div class="drv-rio__meta">
            ${stars(r.rating, fill(this.d.labelStars, { rating: r.rating }))}
            <span class="drv-rio__date">${esc(r.human_date || '')}</span>
          </div>
          ${title}${body}
          ${photos ? `<div class="drv-rio__photos">${photos}</div>` : ''}
          <div class="drv-rio__foot">
            ${r.product_name ? `<span class="drv-rio__product">${esc(r.product_name)}</span>` : ''}
            ${recommend}
          </div>
        </div>
      </li>`;
  }
}

const start = () => document.querySelectorAll('[data-drv-rio]:not([data-rio-ready])').forEach((el) => {
  el.dataset.rioReady = '1';
  new DrvRio(el);
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
document.addEventListener('shopify:section:load', start);
